import React, { Fragment, memo } from 'react';
import { Box, BlockStack, InlineStack, Text, Divider } from '@shopify/polaris';
import { LibraryNotification } from './stores/libraryNotificationsStore';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { AssetCreationToolName } from '@/types/media';
import SupportLink from '@/components/SupportLink';

type NotificationsModalProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  notifications: LibraryNotification[];
  source: string;
};

const TOOL_NAME: Partial<Record<AssetCreationToolName, string>> = {
  [AssetCreationToolName.tryOnProductImage]: 'Product Try On',
  [AssetCreationToolName.tryOnAvatar]: 'Custom Model',
  [AssetCreationToolName.personalAvatarAiImage]: 'Personal Influencer',
  [AssetCreationToolName.animateImage]: 'Image Animation',
  [AssetCreationToolName.textToImage]: 'Generate image',
  [AssetCreationToolName.textToVideo]: 'Text to Video',
  [AssetCreationToolName.replaceImageBackground]: 'Background Replacement',
};

const formatDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${month}/${day}/${year} ${hours}:${minutes}`;
};

const NotificationsModal = ({ isOpen, setIsOpen, notifications, source }: NotificationsModalProps) => {
  return (
    <Modal open={isOpen} onHide={() => setIsOpen(false)}>
      <TitleBar title={`${source} Notifications`} />
      <Box paddingBlock="600" paddingInline="400">
        <BlockStack gap="600">
          <BlockStack gap="400">
            {notifications.map((notification, index) => (
              <Fragment key={notification.id}>
                <InlineStack align="space-between" wrap={false}>
                  <BlockStack gap="100">
                    <Text as="span" variant="headingSm">
                      {notification.description}
                      {TOOL_NAME[notification.toolUsed] ? ` (${TOOL_NAME[notification.toolUsed]})` : ''}
                    </Text>
                    <Text as="span" tone="critical">
                      {notification.error
                        ? sanitizeErrorMessage(notification.error)
                        : 'Generation failed, please try again or contact our support.'}
                    </Text>
                  </BlockStack>
                  {!!notification.generatedAt && (
                    <Box minWidth="120px">
                      <Text as="span" tone="subdued" alignment="end">
                        {formatDate(new Date(notification.generatedAt))}
                      </Text>
                    </Box>
                  )}
                </InlineStack>
                {index < notifications.length - 1 && <Divider />}
              </Fragment>
            ))}
          </BlockStack>
          <SupportLink />
        </BlockStack>
      </Box>
    </Modal>
  );
};

export default memo(NotificationsModal);

function sanitizeErrorMessage(error: string): string {
  const message = error.toLowerCase();
  if (message.startsWith('enospc')) return 'Generation failed due to insufficient disk space, please contact support.';
  if (message.startsWith('enoent')) return 'Generation failed due to missing media file, please contact support.';
  if (message.startsWith('epipe')) return 'Generation failed due to a processing error, please contact support.';
  if (message.startsWith('econnreset')) return 'Generation failed due to a processing error, please contact support.';
  if (message.startsWith('econnrefused')) return 'Generation failed due to a processing error, please contact support.';
  if (message.startsWith('econnaborted')) return 'Generation failed due to a processing error, please contact support.';
  if (message.startsWith('unsupported influencer source'))
    return 'Unsupported influencer source, please try again or contact support.';
  if (message.startsWith('request failed with status code'))
    return 'Generation failed due to a processing error, please contact support.';
  if (message.includes('the level of configured provisioned throughput for the table was exceeded')) {
    return 'Service is overloaded, please try again later or contact support.';
  }
  if (message.startsWith('default source is not found')) {
    return 'Generation failed due to missing media file, please contact support.';
  }
  if (message.startsWith('invalid media type')) {
    return 'Generation failed due to missing media file, please contact support.';
  }
  if (message.startsWith('states.timeout')) {
    return 'Generation timed out, please try again or contact support.';
  }
  if (message.startsWith('ffmpeg')) return 'Generation failed due to a processing error, please contact support.';
  if (message.startsWith('cannot read properties of'))
    return 'Generation failed due to a processing error, please contact support.';
  if (message.includes('Traceback (most recent call last)')) {
    return 'Generation failed due to a processing error, please contact support.';
  }
  if (message.includes('runtime exited with error')) {
    return 'Generation failed due to a processing error, please contact support.';
  }
  if (message.includes('error received from worker')) {
    return 'Generation failed due to a processing error, please contact support.';
  }
  if (message.includes("we don't support try-on for accessories")) {
    return 'This product type is not supported. Please try a top or bottom product.';
  }

  // Remove technical details from error messages
  return error
    .replace(/at \S+ \(.*?\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
